import React, { FC, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { Checkbox } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';
import { AnimatedScreen, useToast } from 'Shared/components';
import { getRefetchPartnerQuery } from 'Admin/components/Router';
import { getGraphqlErrors, PartnerContext } from 'Admin/utils';

import styles from './styles.module.css';
import { UPDATE_PARTNER_PERMISSION } from 'Admin/Partners/graphql';

type PartnerPermissionsType = Record<
  string,
  Array<{ name: string; internal?: boolean; label?: string }>
>;

const sections: PartnerPermissionsType = {
  policies: [
    { name: 'policies.edit_garaging_address' },
    { name: 'policies.edit_multiline_discount' },
    { name: 'policies.cancellation.customer_initiated' },
  ],
  vehicles: [
    { name: 'vehicles.add' },
    { name: 'vehicles.remove' },
    { name: 'vehicles.edit_use_type' },
    { name: 'vehicles.edit_safety_features' },
    { name: 'vehicles.edit_rideshare' },
    { name: 'vehicles.edit_ownership', label: 'Edit Ownership' },
    { name: 'vehicles.edit_lienholder' },
    { name: 'vehicles.edit_mileage', label: 'Edit Mileage' },
    { name: 'vehicle_inspection.edit', internal: true },
  ],
  drivers: [
    { name: 'drivers.add' },
    { name: 'drivers.remove', label: 'Remove Driver' },
    { name: 'drivers.exclude', label: 'Exclude Driver' },
    { name: 'drivers.edit_driver_license', label: 'Edit Driver License' },
    { name: 'drivers.edit_details' },
    { name: 'drivers.edit_report_details', label: 'Edit Report Details' },
    { name: 'drivers.edit_driver_status', internal: true },
  ],
  coverages: [
    { name: 'coverages.edit_policy_coverages' },
    { name: 'coverages.edit_vehicle_coverages' },
    { name: 'Edit Collision Deductible', internal: true },
  ],
  payments: [
    { name: 'policies.manage_and_process_payment_info' },
    { name: 'policies.manage_autopay' },
  ],
};

export const Permissions: FC = () => {
  const { user } = useAuth0();
  const { currentPartner } = useContext(PartnerContext);
  const { toast } = useToast();
  const refetchCurrentPartnerQuery = getRefetchPartnerQuery(
    currentPartner?.id,
    user!.effectiveGrants
  );
  const [updatePartnerPermission] = useMutation<
    {},
    {
      partnerId: string;
      permissionId: string;
      revoked: boolean;
    }
  >(UPDATE_PARTNER_PERMISSION, {
    awaitRefetchQueries: true,
    refetchQueries: [refetchCurrentPartnerQuery],
  });

  if (!currentPartner) return null;

  return (
    <AnimatedScreen testId="partner-permissions-page">
      <h1>Permissions</h1>
      <div className={styles.table}>
        <div className={styles.tableHeader}>
          <div className={styles.left}>
            <div className={styles.tableHeaderLabel}>Permission Type</div>
          </div>
          <div className={styles.right}>
            <div className={styles.tableHeaderLabel}>Licensed Agent/Staff</div>
          </div>
        </div>
        {Object.keys(sections).map(section => (
          <div className={styles.section} key={section}>
            <div className={styles.sectionHeader}>{section}</div>
            {sections[section].map(permission => {
              if (permission.internal) {
                return (
                  <div className={styles.row} key={permission.name}>
                    <div className={styles.left} data-testid={permission.name}>
                      {permission.name}
                    </div>
                    <div className={styles.right}>
                      <div
                        className={styles.internal}
                        data-testid={`internal-label-${permission.name}`}
                      >
                        Internal Clearcover Advocates only
                      </div>
                    </div>
                  </div>
                );
              }

              const permissionDetails = currentPartner.partnerPermissions.find(
                partnerPermission => partnerPermission.name === permission.name
              );

              return permissionDetails ? (
                <div className={styles.row} key={permission.name}>
                  <div className={styles.left}>
                    {permissionDetails.displayName}
                  </div>
                  <div className={styles.right}>
                    <div className={styles.toggleWrapper}>
                      <Checkbox
                        className={styles.toggle}
                        toggle
                        hideLabel
                        name={permissionDetails.name}
                        label={permissionDetails.displayName}
                        value={!permissionDetails.revoked}
                        onChange={value => {
                          updatePartnerPermission({
                            variables: {
                              partnerId: currentPartner.id,
                              permissionId: permissionDetails.id,
                              revoked: !value,
                            },
                          }).catch(err => {
                            toast({
                              type: 'error',
                              icon: 'error',
                              message: getGraphqlErrors(err),
                            });
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.row} key={permission.name}>
                  <div className={styles.left}>{permission.label}</div>
                  <div className={styles.right}>
                    <div className={styles.toggleWrapper}>
                      <Checkbox
                        className={styles.toggle}
                        toggle
                        hideLabel
                        name={permission.name}
                        label={permission.label || ''}
                        value={false}
                        disabled
                        onChange={() => {}}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </AnimatedScreen>
  );
};
